/* eslint-disable no-debugger */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
/* eslint-disable quotes */
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import PaymentsActions from "../../../redux/reducers/payments.reducer";
import MoldalActions from "../../../redux/reducers/modal.reducer";
import IssuerPaymentModel from "../../../lib/models/issuerPayment.model";
import theme from "../../../styles/LightTheme";
import useUserIp from "../../../hooks/useUserIp";

/**
 * Hook para manejar acciones de pagos
 * Centraliza toda la lógica de negocio: pagar, descargar, aprobar
 */
export const usePaymentActions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { state: stateFromModel } = IssuerPaymentModel;
  const userEmail = useSelector((state) => state.user?.userInfo?.user?.email || "");
  const { userIp } = useUserIp();

  /**
   * Maneja el clic en el botón "Pagar"
   * Recibe paymentData del PaymentCard con toda la info necesaria
   */
  const handleClickPay = useCallback(
    (
      campaignId,
      issuerPayment,
      isOverdue = false,
      overdueTotal = 0,
      paymentData = {}
    ) => {
      const { amount, paymentType } = paymentData;

      // Calcular el optionValue según el tipo de pago
      // Positivo para pago total, negativo para custom
      const optionValue =
        paymentType === "total" ? issuerPayment.id : issuerPayment.id * -1;

      // Detectar si es pago parcial en mora
      // Condiciones: custom + overdue + amount < overdueTotal
      const isPartialOverdue =
        paymentType === "custom" &&
        isOverdue &&
        amount > 0 &&
        amount < overdueTotal;

      dispatch(
        PaymentsActions.setCampaignSelected(
          campaignId,
          issuerPayment,
          optionValue,
          isOverdue,
          isOverdue ? overdueTotal : amount,
          isPartialOverdue
        )
      );

      if (paymentType === "custom") {
        dispatch(PaymentsActions.setOtherAmount(amount));

        if (isPartialOverdue) {
          sessionStorage.setItem("paymentType", "partial_overdue_payment");
        }
      }

      const goToConfirm = [
        stateFromModel.approved_by_timeout,
        stateFromModel.issuer_approved,
        stateFromModel.rejected,
        stateFromModel.late_payment
      ].includes(issuerPayment.state);

      // Para pagos NO aprobados (is_approved = false), NO navegamos aquí
      // La navegación la hace el epic approvePayment después de aprobar
      if (!issuerPayment.is_approved) {
        return;
      }

      if (goToConfirm) {
        return dispatch(push("/confirm-pay-amount"));
      }
    },
    [dispatch, stateFromModel]
  );

  /**
   * Configura la campaña seleccionada sin navegar
   * Usado antes de aprobar un pago pending_approve
   */
  const setupCampaignForPayment = useCallback(
    (
      campaignId,
      issuerPayment,
      isOverdue = false,
      overdueTotal = 0,
      paymentData = {}
    ) => {
      const { amount, paymentType } = paymentData;

      const optionValue =
        paymentType === "total" ? issuerPayment.id : issuerPayment.id * -1;

      const isPartialOverdue =
        paymentType === "custom" &&
        isOverdue &&
        amount > 0 &&
        amount < overdueTotal;

      const setCampaignAction = PaymentsActions.setCampaignSelected(
        campaignId,
        issuerPayment,
        optionValue,
        isOverdue,
        isOverdue ? overdueTotal : amount,
        isPartialOverdue
      );

      dispatch(setCampaignAction);

      if (paymentType === "custom") {
        dispatch(PaymentsActions.setOtherAmount(amount));

        if (isPartialOverdue) {
          sessionStorage.setItem("paymentType", "partial_overdue_payment");
        }
      }
    },
    [dispatch]
  );

  /**
   * Descarga archivo Excel de derechos patrimoniales
   * Recibe datos de campaña desde PaymentCard
   */
  const handleDownloadExcel = useCallback(
    (campaignData) => {
      const { campaignId, userId, userEmail, issuerPaymentId, isOverdue } = campaignData;
      const modalGeneral = {
        clearBlur: true,
        css: {
          backgroundColor: theme.transparentMartinique
        }
      };

      dispatch(
        MoldalActions.setConditionalModalState(
          true,
          "ModalLoadingCertificate",
          {
            ...modalGeneral,
            description: t("searchInvestDetail.download")
          }
        )
      );

      dispatch(
        PaymentsActions.downloadFileExcel({
          isLoading: false,
          campaignId,
          userId,
          userEmail,
          issuerPaymentID: issuerPaymentId,
          isOverdue
        })
      );
    },
    [dispatch, t]
  );
  
  /**
   * Aprueba un issuer_payment usando el epic de Redux
   * Solo despacha la acción de aprobación.
   * La configuración de campaña y navegación se hacen DESPUÉS
   * de que la aprobación sea exitosa (en el useEffect de MyPayments).
   */
  const handleApprovePayment = useCallback(
    (issuerPaymentId) => {
      dispatch(PaymentsActions.approveIssuerPayment({ issuerPaymentId }));
    },
    [dispatch]
  );

  /**
   * Descarga tabla de amortización de la campaña
   * Recibe datos desde PaymentCard (barra de plan de pagos)
   */
  const handleDownloadAmortization = useCallback(
    (campaignData) => {
      const { campaignId } = campaignData;
      const modalGeneral = {
        clearBlur: true,
        css: {
          backgroundColor: theme.transparentMartinique
        }
      };

      dispatch(
        MoldalActions.setConditionalModalState(
          true,
          "ModalLoadingCertificate",
          {
            ...modalGeneral,
            description: t("searchInvestDetail.download")
          }
        )
      );

      dispatch(
        PaymentsActions.downloadAmortizationTable({
          isLoading: false,
          campaignId,
          userEmail,
          userIp
        })
      );
    },
    [dispatch, t, userEmail, userIp]
  );

  /**
   * Navega al historial de pagos
   */
  const handleClickLink = useCallback(() => {
    dispatch(push("/payments"));
  }, [dispatch]);

  /**
   * Navega a la página principal
   */
  const handleBack = useCallback(() => {
    dispatch(push("/"));
  }, [dispatch]);

  return {
    handleClickPay,
    handleDownloadExcel,
    handleDownloadAmortization,
    handleApprovePayment,
    handleClickLink,
    handleBack,
    setupCampaignForPayment
  };
};
